/* eslint-disable react/jsx-key */
import { type ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createKey, readKey } from '@/api/params';
import useSWR, { mutate } from 'swr';
import { getFetcher } from '@/api/fetchers';
import { Button, Input, MultiSelect, Select, Text } from '@mantine/core';
import { List } from '@/components/List/List';
import { SortHeader } from '@/components/List/Header/SortHeader';
import { TextHeader } from '@/components/List/Header/TextHeader';
import { MappingToComboBoxItem } from '@/schemas/converters/mappingShemasConverter';
import { type EmployeesRequest } from '@/schemas/API/GET/employee/request';
import { type Mapping } from '@/schemas/API/GET/mapping/response';
import {
  type Employee,
  type Employees,
} from '@/schemas/API/GET/employee/response';

const EmployeeNameCell = (employee: Employee) => (
  <Text>
    {employee.lastName} {employee.firstName}
  </Text>
);

const EmployeeAddressCell = (employee: Employee) => (
  <Text>
    {employee.permAddress.street}, {employee.permAddress.city}
  </Text>
);

const EmployeeContractEndCell = (employee: Employee) => (
  <Text>{employee.contractEnd ? employee.contractEnd : 'Neurčeno'}</Text>
);

const EmployeeRoleCell = (rolesMap: Record<string, string> | undefined) => {
  const Cell = (employee: Employee) => (
    <Text>{rolesMap ? rolesMap[employee.access?.role] : ''}</Text>
  );
  return Cell;
}

type ExtendedEmployeesRequest = EmployeesRequest & { tag?: number[] };

export const EmployeeListPage = (): ReactElement => {
  const navigate = useNavigate();
  const [requestParams, setRequestParams] = useState<ExtendedEmployeesRequest>({
    page: 1,
    size: 10,
  });

  useEffect(() => {
    const queryParams = readKey(window.location.search);
    setRequestParams(currentParams => ({
      ...currentParams,
      ...queryParams,
    }));
  }, []);

  const { data: availableTags } = useSWR('tags', getFetcher);
  const { data: rolesMap } = useSWR<Mapping>('mappings/roles', getFetcher);
  const { data: employees } = useSWR<Employees>(
    '/employees' + createKey(requestParams),
    getFetcher
  );

  useEffect(() => {
    navigate(createKey(requestParams), { replace: true });
  }, [requestParams, navigate]);

  const setRequestParam = <K extends keyof ExtendedEmployeesRequest>(
    fieldName: K,
    value: ExtendedEmployeesRequest[K] | null
  ): void => {
    const newParams = { ...requestParams, [fieldName]: value };
    setRequestParams(newParams);

    mutate('/employees' + createKey(newParams), employees, false);
  };

  const SearchBar = (
    <Input.Wrapper label="Vyhledat">
      <Input
        placeholder="např: Václav"
        value={requestParams?.query}
        onChange={e => setRequestParam('query', e.currentTarget.value)}
      />
    </Input.Wrapper>
  );

  const RoleSelector = (
    <Select
      label="Role"
      value={requestParams.role}
      onChange={selected => setRequestParam('role', selected)}
      data={MappingToComboBoxItem(rolesMap)}
      clearable
    />
  );

  const TagSelector = (
    <MultiSelect
      label="Tagy"
      value={requestParams.tag?.map((tag: number) => tag.toString())}
      onChange={selected =>
        setRequestParam(
          'tag',
          selected.map(s => Number(s))
        )
      }
      data={availableTags?.content.map((tag: any) => ({
        value: tag.id.toString(),
        label: tag.name,
      }))}
      clearable
    />
  );

  return (
    <div className="h-full p-8 overflow-auto">
      <List
        name="Zaměstnanci"
        pagination={{
          page: requestParams.page ?? 1,
          setPage: page => setRequestParam('page', page),
          pageTotal: employees?.totalPages ?? 1,
        }}
        selectors={[SearchBar, RoleSelector, TagSelector]}
        loading={!availableTags || !employees}
        controls={[
          <Button
            onClick={() => {
              navigate('/employees/new-employee');
            }}
          >
            Přidat osobu
          </Button>,
        ]}
        columns={[
          <SortHeader
            label="Příjmení a jméno"
            fields={['lastName', 'firstName']}
            callback={sort => setRequestParam('sort', sort)}
            currentSort={requestParams.sort ?? []}
          />,
          <TextHeader>Bydliště</TextHeader>,
          <TextHeader>Konec Smlouvy</TextHeader>,
          <TextHeader>Role</TextHeader>,
        ]}
        content={{
          items: employees?.content ?? [],
          onClick: (employee: Employee) =>
            navigate(`/employees/${employee.id}/personal`),
          renders: [
            EmployeeNameCell,
            EmployeeAddressCell,
            EmployeeContractEndCell,
            EmployeeRoleCell(rolesMap),
          ],
        }}
      />
    </div>
  );
};
